// Shared presentational bits — the mockup's chip / switch / modal / sparkline markup.
import { useEffect, type ReactNode } from 'react';
import { CUTOVER, ago } from './model';
import type { Dial, DisplayVerdict, Pipeline, StepRecord, TtsStatus } from './types';

/** The registration state of one of OUR webhooks, as one chip. `receiving` = no listing exists for the source
 *  (Calendly), but a signed delivery arrived recently. */
export function RegistrationChip({ entry, short }: { entry: { registered: boolean | 'unknown' | 'receiving'; lastReceivedAt?: string | null; source: string }; short?: boolean }) {
  if (entry.registered === true) return <span className="chip ok">{short ? 'yes' : 'registered'}</span>;
  if (entry.registered === 'receiving') {
    return (
      <span className="chip ok" title={`${entry.source} subscriptions are not listable from here — the last signed delivery is the evidence`}>
        receiving{entry.lastReceivedAt ? ` · ${ago(entry.lastReceivedAt)}` : ''}
      </span>
    );
  }
  if (entry.registered === 'unknown') return <span className="chip">{short ? 'unknown' : 'registration unknown'}</span>;
  if (entry.lastReceivedAt !== undefined) {
    return (
      <span className="chip warn" title={`no signed ${entry.source} delivery in the last 7 days`}>
        no traffic{entry.lastReceivedAt ? ` · last ${ago(entry.lastReceivedAt)}` : ''}
      </span>
    );
  }
  return <span className="chip warn">{short ? 'no' : 'not registered'}</span>;
}

export function VerdictChip({ v }: { v: DisplayVerdict | string }) {
  if (v === 'off') return <span className="chip">off</span>;
  if (v === 'external') return <span className="chip">external</span>;
  const cls = v === 'green' ? 'ok' : v === 'amber' ? 'warn' : 'bad';
  return (
    <span className={`chip ${cls}`}>
      <span className="d" />
      {v}
    </span>
  );
}

export function EnvChip({ env }: { env: string }) {
  return <span className={`chip ${env === 'prod' ? 'warn' : 'info'}`}>{env}</span>;
}

export function DialChips({ dial, parked }: { dial: Dial | undefined; parked?: boolean }) {
  if (parked) return <span className="chip warn">parked</span>;
  if (!dial) return <span className="chip">unknown</span>;
  return (
    <>
      {dial.enabled ? (
        <span className="chip ok">enabled</span>
      ) : (
        <span className="chip bad">disabled</span>
      )}{' '}
      {dial.dryRun ? <span className="chip info">dry-run</span> : <span className="chip ok">LIVE</span>}
    </>
  );
}

const STATUS_CLASS: Record<string, string> = {
  done: 'ok',
  'dry-run': 'info',
  failed: 'bad',
  pending: 'warn',
  reassigned: 'ok',
  held: 'warn',
};

export function StatusChip({ status }: { status: TtsStatus | string }) {
  const cls = STATUS_CLASS[status];
  return <span className={cls ? `chip ${cls}` : 'chip'}>{status}</span>;
}

function stepClass(r: StepRecord): string {
  if (r.error) return 'err';
  if (r.would) return 'dry';
  if (r.skipped) return 'skip';
  if (r.ok) return 'ok';
  return '';
}

export function StepChips({ steps }: { steps: Record<string, StepRecord> }) {
  return (
    <span className="steps">
      {Object.entries(steps ?? {}).map(([n, r]) => (
        <span
          key={n}
          className={`st ${stepClass(r)}`}
          title={r.error || r.skipped || (r.would ? 'dry-run' : 'ok')}
        >
          {n}
        </span>
      ))}
    </span>
  );
}

export function Switch({
  on,
  warn,
  disabled,
  title,
  label,
  onToggle,
}: {
  on: boolean;
  warn?: boolean;
  disabled?: boolean;
  title?: string;
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`switch ${warn ? 'warn ' : ''}${on ? 'on' : ''}`}
      disabled={disabled}
      title={title}
      aria-label={label}
      aria-pressed={on}
      onClick={onToggle}
    />
  );
}

export function Modal({
  title,
  children,
  actions,
  onClose,
}: {
  title: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true">
        <div className="box">
          <h3>{title}</h3>
          {children}
          <div className="acts">{actions}</div>
        </div>
      </div>
    </>
  );
}

export function Toast({ text }: { text: string }) {
  return <div className="toast">{text}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** Inline `code` spans inside the cutover copy, without dangerouslySetInnerHTML. */
export function Ticks({ text }: { text: string }) {
  const parts = text.split(/`([^`]+)`/g);
  return (
    <span>
      {parts.map((p, i) => (i % 2 ? <code key={i}>{p}</code> : <span key={i}>{p}</span>))}
    </span>
  );
}

export function CutoverList({
  state,
  onChange,
}: {
  state: Record<string, boolean>;
  onChange: (key: string, checked: boolean) => void;
}) {
  return (
    <>
      {CUTOVER.map(([k, t]) => (
        <label className="chk" key={k}>
          <input
            type="checkbox"
            checked={!!state[k]}
            onChange={(e) => onChange(k, e.target.checked)}
          />
          <Ticks text={t} />
        </label>
      ))}
    </>
  );
}

/** Expected-submissions ramp with the amber/red thresholds drawn in. */
export function Sparkline({ exp, amber, red }: { exp: number; amber: number; red: number }) {
  const W = 320;
  const H = 54;
  const max = Math.max(red * 1.15, exp * 1.1, 1);
  const y = (v: number): number => H - 6 - (v / max) * (H - 12);
  const pts: string[] = [];
  for (let i = 0; i <= 20; i++) pts.push(`${(i / 20) * W},${y((exp * i) / 20)}`);
  const line = pts.join(' ');
  return (
    <svg
      className="spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-label="expected submissions accumulating"
    >
      <line x1="0" x2={W} y1={y(amber)} y2={y(amber)} stroke="var(--warn)" strokeDasharray="3 3" strokeWidth="1" />
      <line x1="0" x2={W} y1={y(red)} y2={y(red)} stroke="var(--bad)" strokeDasharray="3 3" strokeWidth="1" />
      <polygon points={`0,${H - 6} ${line} ${W},${H - 6}`} fill="var(--primary-soft)" />
      <polyline points={line} fill="none" stroke="var(--primary)" strokeWidth="2" />
      <circle cx={W} cy={y(exp)} r="3" fill="var(--primary)" />
      <text x="4" y={y(red) - 3} fontSize="9" fill="var(--bad)" fontFamily="var(--mono)">
        red {red}
      </text>
      <text x="4" y={y(amber) - 3} fontSize="9" fill="var(--warn)" fontFamily="var(--mono)">
        amber {amber}
      </text>
    </svg>
  );
}

export function StepFlow({ pipeline }: { pipeline: Pipeline }) {
  return (
    <>
      {Object.entries(pipeline.steps ?? {}).map(([branch, names]) => (
        <div className="well" style={{ marginBottom: 8 }} key={branch}>
          <div
            className="dim"
            style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em' }}
          >
            {branch}
          </div>
          <div className="row" style={{ gap: 4, marginTop: 4 }}>
            {names.map((n, i) => (
              <span key={n}>
                <span className="st">{n}</span>
                {i < names.length - 1 ? <span className="faint"> → </span> : null}
              </span>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/** One line per view: loading / error / nothing. */
export function StateLine({ loading, error }: { loading: boolean; error: string | null }) {
  if (error) return <p className="notice">{error}</p>;
  if (loading) return <p className="note">loading…</p>;
  return null;
}
